import React, { useState, useEffect } from 'react';

const Toast = ({
  message,
  type,
  duration = 3000,
  onClose,
  position = 'top-right',
  index = 0, // for stacking animation delay
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Progress bar effect
  useEffect(() => {
    if (!isVisible || isHovered) return;

    const interval = setInterval(() => {
      setElapsed((prev) => {
        if (prev >= duration) {
          setIsVisible(false);
          if (onClose) onClose();
          return duration;
        }
        return prev + 100; // update every 100ms
      });
    }, 100);

    return () => clearInterval(interval);
  }, [duration, isVisible, isHovered, onClose]);

  // Auto-dismiss if not hovered
  useEffect(() => {
    if (!isVisible || isHovered) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, isHovered, isVisible, onClose]);

  if (!isVisible) return null;

  const typeClasses = {
    success: 'bg-green-500 text-white border-l-4 border-green-600',
    error: 'bg-red-400 text-white border-l-4 border-red-600',
    warning: 'bg-yellow-500 text-white border-l-4 border-yellow-600',
    info: 'bg-blue-500 text-white border-l-4 border-blue-600',
  };

  const typeIcons = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️',
  };

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  const progress = (elapsed / duration) * 100;

  return (
    <div
      className={`fixed z-50 max-w-sm w-full shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 ${typeClasses[type]} ${positionClasses[position]} transition-opacity duration-300 ease-out`}
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
        opacity: isVisible ? 1 : 0,
        transition: `all 0.3s ease-out ${index * 0.1}s`, 
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="alert"
    >
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 w-full h-1 bg-black bg-opacity-20 rounded-t-lg overflow-hidden">
        <div
          className="h-full bg-white bg-opacity-60 transition-all duration-100 ease-linear"
          style={{ width: `${100 - progress}%` }}
        />
      </div>

      {/* Toast Content */}
      <div className="flex-1 w-0 p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 pt-0.5">
            <span className="text-xl">{typeIcons[type]}</span>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium">{message}</p>
          </div>
        </div>
      </div>

      {/* Close Button */}
      <div className="flex border-l border-opacity-20">
        <button
          onClick={() => {
            setIsVisible(false);
            if (onClose) onClose();
          }}
          className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-white hover:bg-white hover:bg-opacity-20 focus:outline-none"
          aria-label="Close toast"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;